import { secureDelete, secureGet, securePost, securePut } from "../../Config/ApiConfig";

// Local helper to clean filters without breaking arrays (e.g. ['admin'])
const cleanFilters = (filters) => {
    if (!filters) return filters;
    const result = {};
    Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value === null || value === undefined) {
            return;
        }
        if (Array.isArray(value)) {
            const arr = value.filter((v) => v !== null && v !== undefined);
            if (arr.length) {
                result[key] = arr;
            }
        } else {
            result[key] = value;
        }
    });
    return result;
};

export const getMessages = async ({ page, size, conversation_id, after, before, since, until }) => {
    let url = `/conversations/${conversation_id}/messages?sort_by=sent_at&sort_direction=desc&page_size=${size}`;
    if (after) {
        url += `&after=${encodeURIComponent(after)}`;
    } else if (before) {
        url += `&before=${encodeURIComponent(before)}`;
    } else if (since) {
        url += `&since=${encodeURIComponent(since)}`;
    } else if (until) {
        url += `&until=${encodeURIComponent(until)}`;
    } else if (page) {
        url += `&page=${page}`;
    }
    const { data } = await secureGet(url);
    return data;
};


export const getConversations = async ({
    page,
    size,
    channel_id,
    filters = {},
    search_string,
    sort_by,
    sort_direction,
    after,
    before,
    since,
    until,
    signal // Accept AbortController signal for cancellation
}) => {
    // Merge channel_id into filters as per API specification
    const mergedFilters = channel_id ? { ...filters, channel_id } : filters;
    const cleanedFilters = mergedFilters ? cleanFilters(mergedFilters) : undefined;
    const trimmedSearch = typeof search_string === 'string' ? search_string.trim() : search_string;
    const effectiveSortBy = sort_by || 'updated_time';
    const effectiveSortDirection = sort_direction || 'desc';
    let url = `/conversations?sort_by=${encodeURIComponent(effectiveSortBy)}&sort_direction=${encodeURIComponent(effectiveSortDirection)}&page_size=${size}`;

    if (after) {
        url += `&after=${encodeURIComponent(after)}`;
    }

    if (before) {
        url += `&before=${encodeURIComponent(before)}`;
    }

    if (since) {
        url += `&since=${encodeURIComponent(since)}`;
    }

    if (until) {
        url += `&until=${encodeURIComponent(until)}`;
    }

    if (page) {
        url += `&page=${page}`;
    }

    if (cleanedFilters && Object.keys(cleanedFilters).length) {
        url += `&filters=${encodeURIComponent(JSON.stringify(cleanedFilters))}`;
    }

    if (trimmedSearch) {
        url += `&search_string=${encodeURIComponent(trimmedSearch)}`;
    }

    // Pass signal to axios for request cancellation
    const { data } = await secureGet(url, signal ? { signal } : {});
    return data;
};

export const getConversationById = async (id) => {
    const { data } = await secureGet(`/conversations/${id}`);
    return data;
};

export const getAgents = async ({ page, size, search }) => {
    const { data } = await secureGet(`/agents?page=${page}&page_size=${size}${search ? "&search_string=" + search : ''}`);
    return data;
};

export const updateContact = async (id, body) => {
    const { data } = await securePut(`/contacts/${id}`, body);
    return data;
}

export const reportContact = async (id, payload) => {
    const { data } = await securePost(`/contacts/${id}/report`, payload);
    return data;
}

export const deleteConversation = async (id) => {
    const { data } = await secureDelete(`/conversations/${id}`);
    return data;
}

export const sendMessage = async (conversation_id, body) => {
    const { data } = await securePost(`/conversations/${conversation_id}/messages`, body);
    return data;
};

export const uploadFile = async (file) => {
    const formData = new FormData();
    if (file && file.name) {
        formData.append('file', file, file.name);
    } else {
        formData.append('file', file);
    }

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };

    const { data } = await securePost('/file-uploads', formData, config);
    return data;
};

// Bulk upload multiple files in one request
export const uploadFilesBulk = async (files = []) => {
    const formData = new FormData();
    (files || []).forEach((file) => {
        if (file && file.name) {
            formData.append('files', file, file.name);
        } else {
            formData.append('files', file);
        }
    });

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };

    const { data } = await securePost('/file-upload/bulk', formData, config);
    // Expected shape:
    // { uploaded: [<same object as single upload returns>], failed: [{ filename, message, message_code, message_data }] }
    return data;
};

export const deleteFileUpload = async (file_upload_id) => {
    const { data } = await secureDelete(`/file-uploads/${file_upload_id}`);
    return data;
};

export const markConversationSeen = async (conversation_id, is_seen_by_user = true, message_id) => {
    const body = { is_seen_by_user, message_id };
    const { data } = await securePut(`/conversations/${conversation_id}/seen`, body);
    return data;
};

export const markConversationUnseen = async (conversation_id, message_id) => {
    const body = { is_seen_by_user: false, message_id };
    const { data } = await securePut(`/conversations/${conversation_id}/seen`, body);
    return data;
};

export const deleteMessage = async (message_id) => {
    const { data } = await secureDelete(`/messages/${message_id}`);
    return data;
};

// Link file uploads to a conversation (for draft attachments)
export const linkFileUploads = async (conversation_id, file_upload_ids) => {
    const { data } = await securePost(`/conversations/${conversation_id}/file-uploads`, { file_upload_ids });
    return data;
};

// Unlink file uploads from a conversation
export const unlinkFileUploads = async (conversation_id, file_upload_ids) => {
    const { data } = await secureDelete(`/conversations/${conversation_id}/file-uploads`, { data: { file_upload_ids } });
    return data;
};

// Beautify generic text via backend POST /beautify
export const beautifyText = async (text, options = {}) => {
    const payload = { text, ...options };
    const { data } = await securePost(`/beautify`, payload);
    return data;
};

// Beautify a message in the context of a specific conversation
// POST /conversations/:conversation_id/beautify-message
export const beautifyConversationMessage = async (conversation_id, content, options = {}) => {
    const payload = { content, ...options };
    const { data } = await securePost(`/conversations/${conversation_id}/beautify-message`, payload);
    return data;
};